import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { memo, useMemo } from "react";
import FormSectionDivider from "../formSectionDivider";
import {
  buildTramosResumenRows,
  formatDistanciaResumen,
} from "./fptTrasladosUtils";

const headerCellSx = {
  fontWeight: 700,
  fontSize: "0.7rem",
  letterSpacing: "0.04em",
  color: "text.secondary",
  borderBottom: "1px solid",
  borderColor: "divider",
  py: 1,
};

const PuntoCell = ({ label, address }) => (
  <Box>
    <Typography variant="body2" fontWeight={700} lineHeight={1.3}>
      {label || "—"}
    </Typography>
    <Typography variant="caption" color="text.secondary" lineHeight={1.35}>
      {address || "—"}
    </Typography>
  </Box>
);

const TramoIndexBadge = ({ index }) => (
  <Box
    sx={{
      width: 28,
      height: 28,
      borderRadius: "50%",
      bgcolor: "grey.200",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 700,
      fontSize: "0.85rem",
      color: "text.primary",
    }}>
    {index}
  </Box>
);

const TramosAGenerarResumen = ({ puntos = [] }) => {
  const rows = useMemo(() => buildTramosResumenRows(puntos), [puntos]);
  const distanciaTotalMeters = useMemo(
    () =>
      rows.reduce(
        (total, row) =>
          Number.isFinite(row.distanciaMeters)
            ? total + row.distanciaMeters
            : total,
        0,
      ),
    [rows],
  );

  if (rows.length === 0) return null;

  return (
    <Box sx={{ flexShrink: 0, mb: 2 }}>
      <FormSectionDivider title="Tramos a generar" />
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "background.paper",
        }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell sx={{ ...headerCellSx, width: 72 }}>TRAMO</TableCell>
              <TableCell sx={headerCellSx}>ORIGEN</TableCell>
              <TableCell sx={headerCellSx}>DESTINO</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 150 }}>
                FECHA Y HORA
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 100 }}>RETORNO</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 110 }} align="right">
                DISTANCIA
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.tramoIndex}
                sx={{
                  bgcolor: row.esRetorno ? "#fff8e1" : "inherit",
                  "&:last-child td": { borderBottom: 0 },
                }}>
                <TableCell>
                  <TramoIndexBadge index={row.tramoIndex} />
                </TableCell>
                <TableCell>
                  <PuntoCell
                    address={row.origenAddress}
                    label={row.origenLabel}
                  />
                </TableCell>
                <TableCell>
                  <PuntoCell
                    address={row.destinoAddress}
                    label={row.destinoLabel}
                  />
                </TableCell>
                <TableCell>
                  {row.fechaHora ? (
                    <Typography variant="body2">
                      {moment(row.fechaHora).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                  ) : (
                    <Chip
                      label="a definir"
                      size="small"
                      sx={{
                        bgcolor: "#ffe0b2",
                        color: "#e65100",
                        fontWeight: 600,
                      }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.esRetorno ? "Sí" : "No"}
                    size="small"
                    sx={{
                      bgcolor: row.esRetorno ? "#ffe0b2" : "grey.200",
                      color: row.esRetorno ? "#e65100" : "text.secondary",
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={formatDistanciaResumen(row.distanciaMeters)}
                    size="small"
                    sx={{
                      bgcolor: "#e8f5e9",
                      color: "#2e7d32",
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 3,
            px: 2,
            py: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "grey.50",
          }}>
          <Typography variant="body2" color="text.secondary">
            Tramos:{" "}
            <Box component="span" fontWeight={700} color="text.primary">
              {rows.length}
            </Box>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Distancia total estimada:{" "}
            <Box component="span" fontWeight={700} color="text.primary">
              {formatDistanciaResumen(distanciaTotalMeters)}
            </Box>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(
  TramosAGenerarResumen,
  (prev, next) =>
    prev.puntos === next.puntos &&
    (prev.puntos?.length ?? 0) === (next.puntos?.length ?? 0),
);
